/** @jsxRuntime classic */
/** @jsx jsx */
import { FC } from 'react';
import { jsx } from '@emotion/react';
import { AnswerTemplateCustomerDto } from '@bondsports/types';
import { DateTimeFormats, convertUtcTimestampToTimezone } from '@bondsports/date-time';
import { ETypography, ETypographyColor } from '../../../types';
import { Typography } from '../../../atoms';
import { CustomerDetailLine } from './CustomerDetailLine';
import { sectionCss } from '../styles';
import { i18n } from '../../../lib/i18n';

interface IPrintHeaderCustomer extends AnswerTemplateCustomerDto {
	accountAdminEmail?: string | null;
}

interface IProps {
	customer: IPrintHeaderCustomer;
	dateSigned: string | Date;
	timezone: string;
}

interface IDetailRow {
	key: string;
	label: string;
	value?: string | null;
}

/**
 * Combined participant + account-admin lines for the signed-form print header.
 * @param props - Customer DTO, signed timestamp, and org timezone
 * @returns Header details block
 */
export const CustomerDetails: FC<IProps> = (props: IProps) => {
	const { customer, dateSigned, timezone } = props;
	const templateLabels = i18n.applicationTemplate;
	const labels = templateLabels.customerDetails;
	const formattedDate = templateLabels.dateSigned(
		convertUtcTimestampToTimezone(dateSigned, timezone, DateTimeFormats.DAY_FORMAT),
		convertUtcTimestampToTimezone(dateSigned, timezone, DateTimeFormats.H12_AMPM_UPPERCASE)
	);

	const hasAccountAdmin = Boolean(customer.primary?.toString().trim() || customer.accountAdminEmail?.toString().trim());
	const participantEmailLabel = hasAccountAdmin ? labels.details.participantEmail : labels.details.emailAddress;

	const rows: IDetailRow[] = [
		{ key: 'dateSigned', label: labels.details.dateSigned, value: formattedDate },
		{ key: 'participantName', label: labels.details.participantName, value: customer.participantName },
		{ key: 'emailAddress', label: participantEmailLabel, value: customer.emailAddress },
		{ key: 'primary', label: labels.details.primary, value: customer.primary },
		{ key: 'accountAdminEmail', label: labels.details.accountAdminEmail, value: customer.accountAdminEmail },
		{ key: 'phoneNumber', label: labels.details.phoneNumber, value: customer.phoneNumber },
		{ key: 'address', label: labels.details.address, value: customer.address },
	];

	return (
		<div css={sectionCss} data-testid="application-header-details">
			<Typography color={ETypographyColor.primary} type={ETypography.h4}>
				{labels.title}
			</Typography>
			<div className="customer-details-container">
				{rows
					.filter(row => Boolean(row.value?.toString().trim()))
					.map(row => (
						<CustomerDetailLine key={row.key} title={row.label} data={row.value ?? ''} />
					))}
			</div>
		</div>
	);
};
